import type {
  ProductMappingFreshnessFilter,
  ProductMappingStatus,
} from "@price-rescue/api-contracts";
import {
  PRODUCT_MAPPING_STATUS_SUGGESTED,
  PRODUCT_MAPPING_STATUSES,
} from "@price-rescue/api-contracts";

export type MappingStatus = ProductMappingStatus;
export type MappingFreshness = ProductMappingFreshnessFilter;
export type MappingAction = "APPROVE" | "REJECT" | "DISABLE" | "REMAP" | "CORRECT";
export type ConfidenceBucket = "HIGH" | "MEDIUM" | "LOW";
export type MappingEvidenceSource = "MANUAL" | "REQUEST_EVIDENCE" | "MERCHANT_REVIEW";

export interface MappingProductEvidence {
  title?: string;
  brand?: string;
  model?: string;
  sku?: string;
  gtin?: string;
  upc?: string;
  mpn?: string;
  variantTitle?: string;
  variantAttributes?: Record<string, string>;
  packSize?: string;
  unit?: string;
  condition?: string;
  seller?: string;
  canonicalUrl?: string;
  imageUrl?: string;
  priceMinor?: number;
  salePriceMinor?: number;
  currency?: string;
  availability?: string;
  availableQuantity?: number;
}

export interface MappingFilters {
  competitorId?: string | undefined;
  cursor?: string | undefined;
  domain?: string | undefined;
  freshness?: MappingFreshness | "" | undefined;
  limit?: number | undefined;
  platformProductId?: string | undefined;
  productId?: string | undefined;
  search?: string | undefined;
  sort?: "updated" | "confidence" | "status" | "product" | undefined;
  status?: MappingStatus | "" | undefined;
}

export interface MappingLatestObservation {
  checkedAt?: string;
  succeeded?: boolean;
  errorCode?: string;
  priceMinor?: number;
  currency?: string;
  availability?: string;
}

export interface ProductMapping {
  mappingId: string;
  platformProductId: string;
  platformVariantId: string;
  competitorId: string;
  canonicalUrl: string;
  status: MappingStatus;
  identityConfidence: number;
  confidenceBucket: ConfidenceBucket;
  verifiedAttributes: Record<string, string>;
  merchantEvidence?: MappingProductEvidence;
  competitorEvidence?: MappingProductEvidence;
  identityEvidenceCapturedAt?: string;
  identityEvidenceSource?: MappingEvidenceSource;
  monitoringEnabled: boolean;
  latestObservation?: MappingLatestObservation;
  allowedActions: MappingAction[];
  rejectedSuppression?: { suppressed: boolean; reason: string };
  merchantNote?: string;
}

export interface MappingPage {
  mappings: ProductMapping[];
  nextCursor: string | null;
}

export interface ManualMappingDraft {
  platformProductId: string;
  platformVariantId: string;
  competitorId: string;
  canonicalUrl: string;
  status: MappingStatus;
  reason: string;
  merchantNote?: string;
  verifiedAttributes: Record<string, string>;
  merchantEvidence?: MappingProductEvidence;
  identityEvidenceCapturedAt?: string;
  identityEvidenceSource?: MappingEvidenceSource;
}

export interface MappingDecisionDraft {
  action: MappingAction;
  status: MappingStatus;
  useDirectStatus?: boolean;
  reason: string;
  canonicalUrl?: string | undefined;
  merchantNote?: string;
  overrideHardConflict: boolean;
  verifiedAttributes: Record<string, string>;
}

export interface MappingConflictState {
  mappingId?: string;
  message: string;
  preservedDraft?: MappingDecisionDraft;
  duplicate?: {
    existingMappingId?: string;
    existingStatus?: MappingStatus;
    platformProductId?: string;
    platformVariantId?: string;
    canonicalUrl?: string;
  };
}

export function defaultMappingFilters(): MappingFilters {
  return { limit: 25, sort: "updated" };
}

export function defaultManualMappingDraft(): ManualMappingDraft {
  return {
    platformProductId: "",
    platformVariantId: "",
    competitorId: "",
    canonicalUrl: "",
    status: PRODUCT_MAPPING_STATUS_SUGGESTED,
    reason: "MERCHANT_MANUAL_MAPPING",
    verifiedAttributes: {},
  };
}

export function defaultMappingDecisionDraft(mapping?: ProductMapping): MappingDecisionDraft {
  return {
    action: defaultDecisionAction(mapping),
    status: mapping?.status ?? PRODUCT_MAPPING_STATUS_SUGGESTED,
    reason: "MERCHANT_MAPPING_REVIEW",
    canonicalUrl: mapping?.canonicalUrl,
    merchantNote: mapping?.merchantNote ?? "",
    overrideHardConflict: false,
    verifiedAttributes: { ...(mapping?.verifiedAttributes ?? {}) },
  };
}

export function validateManualMappingDraft(draft: ManualMappingDraft): string[] {
  return [
    required("Product id", draft.platformProductId),
    required("Variant id", draft.platformVariantId),
    required("Competitor id", draft.competitorId),
    validateMappingStatus(draft.status),
    required("Reason", draft.reason),
    validateUrl(draft.canonicalUrl),
  ].filter((issue): issue is string => issue !== undefined);
}

export function validateDecisionDraft(
  draft: MappingDecisionDraft,
  mapping?: ProductMapping,
): string[] {
  return [
    required("Reason", draft.reason),
    validateMappingStatus(draft.status),
    validateAllowedAction(draft, mapping),
    draft.canonicalUrl === undefined ? undefined : validateUrl(draft.canonicalUrl),
  ].filter((issue): issue is string => issue !== undefined);
}

function defaultDecisionAction(
  mapping: ProductMapping | undefined,
): MappingDecisionDraft["action"] {
  return safeAllowedActions(mapping).find(isDecisionAction) ?? "APPROVE";
}

function safeAllowedActions(mapping: ProductMapping | undefined): MappingAction[] {
  return Array.isArray(mapping?.allowedActions) ? mapping.allowedActions : [];
}

function isDecisionAction(action: MappingAction): action is MappingDecisionDraft["action"] {
  return (
    action === "APPROVE" ||
    action === "REJECT" ||
    action === "DISABLE" ||
    action === "REMAP" ||
    action === "CORRECT"
  );
}

function validateAllowedAction(
  draft: MappingDecisionDraft,
  mapping: ProductMapping | undefined,
): string | undefined {
  if (draft.useDirectStatus === true) {
    return undefined;
  }

  if (mapping === undefined || safeAllowedActions(mapping).includes(draft.action)) {
    return undefined;
  }

  return "Selected action is no longer available for this mapping.";
}

function validateMappingStatus(status: MappingStatus): string | undefined {
  return PRODUCT_MAPPING_STATUSES.includes(status) ? undefined : "Mapping status is not supported.";
}

function required(label: string, value: string): string | undefined {
  return value.trim().length === 0 ? `${label} is required.` : undefined;
}

function validateUrl(value: string): string | undefined {
  try {
    const parsed = new URL(value);
    return ["http:", "https:"].includes(parsed.protocol)
      ? undefined
      : "Competitor URL must be HTTP or HTTPS.";
  } catch {
    return "Competitor URL must be valid.";
  }
}
